import React, { useEffect, useState } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Label,
  FormGroup,
  Form,
  Input,
  Button,
  Spinner,
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { actionSubmitExtRef } from 'store/actions/usersActions';
import patientService from 'services/patientService';

/**
 * @component PatientUsersForm
 * @description Form for editing the external references of a patient's associated
 * practices. Since `associated_details` is no longer included in the Redux list
 * payload, this component fetches the full details on mount via
 * `patientService.getPatientDetails` and renders the ext-ref inputs once loaded.
 */
const PatientUsersForm = () => {
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.usersPatientsReducer.editData);
  const { register, handleSubmit } = useForm();
  const history = useHistory();

  const [practices, setPractices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  /**
   * Loads all associated practices for the patient in Redux edit state.
   * Uses a high detailsLimit to capture all practices in a single request.
   */
  useEffect(() => {
    if (!userData?.auth_user_id) return;

    const load = async () => {
      setLoading(true);
      setFetchError(null);
      try {
        const response = await patientService.getPatientDetails(
          userData.auth_user_id,
          0,
          100,
          0,
          1
        );
        
        // Corrección del triple anidamiento de data: Axios -> Express -> Service
        const payload = response?.data?.data;
        const resultData = payload?.data?.[0];
        
        // Cambio clave: apuntamos a 'practices' en lugar de 'associated_details'
        setPractices(resultData?.practices ?? []);
      } catch (e) {
        setFetchError('Could not load practice data.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [userData?.auth_user_id]);

  /**
   * Reduces flat form fields into an array of `{ practice, ext_ref }` objects
   * and dispatches the update action.
   *
   * @param {Object} data - react-hook-form collected values.
   */
  const onSubmit = (data) => {
    const refinedData = Object.entries(data).reduce((acc, [k, v]) => {
      const parts = k.split('_');
      const id = parts[parts.length - 1];
      const keyvalue = parts.slice(0, parts.length - 1).join('_');
      (acc[id] = acc[id] || {})[keyvalue] = v;
      return acc;
    }, []);

    dispatch(actionSubmitExtRef({ ...userData, set_ext_refs: refinedData }));
  };

  return (
    <div className="content user-manual-edit">
      <Card>
        <CardHeader>
          {`${userData?.firstName} ${userData?.lastName} External Reference edition form`}
        </CardHeader>
        <CardBody>
          {loading ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
            </div>
          ) : fetchError ? (
            <div className="text-danger">{fetchError}</div>
          ) : (
            <Form id="user-edit-form" onSubmit={handleSubmit(onSubmit)}>
              {practices.map((p, i) => (
                <React.Fragment key={i}>
                  <Label>External Reference ({p.name || p.practice_name || `Practice ${i + 1}`})</Label>
                  <FormGroup>
                    <Input
                      type="text"
                      name={`ext_ref_${i}`}
                      defaultValue={p.patient_ext_ref}
                      required
                      innerRef={register}
                    />
                    <Input
                      type="hidden"
                      name={`practice_${i}`}
                      defaultValue={p.practiceId} // Cambio clave: 'practiceId' en lugar de 'practice_id'
                      innerRef={register}
                    />
                  </FormGroup>
                </React.Fragment>
              ))}
            </Form>
          )}

          <Button form="user-edit-form" size="sm" color="info" type="submit" disabled={loading}>
            Update
          </Button>

          <Button
            size="sm"
            color="danger"
            onClick={(e) => {
              e.preventDefault();
              history.goBack();
            }}
          >
            Cancel
          </Button>
        </CardBody>
      </Card>
    </div>
  );
};

export default PatientUsersForm;